import React, { useEffect, useState } from "react";
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  FormControl,
  Grid,
  InputAdornment,
  MenuItem,
  Pagination,
  Paper,
  Select,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import useFetchObjects from "../../../../api/useFetchObjects";
import { useAuth } from "../../../../context";
import Loading from "./Loading";
import ErrorPage from "../../../../Components/GeneralComponents/errorPage/ErrorPage";
import FormatDateTime from "../../../../Components/GeneralComponents/DateFormatter/FormatDateTime";
import useDelete from "../../../../api/useDelete";
import { useTranslation } from "react-i18next";
import EditHtPatientProcedure from "./EditHtPatientProcedure";
import { useNavigate } from "react-router-dom";

// Define a style object for table cells with reduced padding and font size
const tableCellStyle = {
  border: "1px solid #ddd",
  textAlign: "center",
  padding: "4px",
  fontSize: "0.8rem",
};

const HtPatientProcedureList = () => {
  const { t } = useTranslation();
  const auth = useAuth();
  const user = auth?.user;
  const token = user?.token;

  // Local states
  const [htPatientId, setHtPatientId] = useState(null);
  const [keyword, setKeyword] = useState("");
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();

  // Filters
  const [type, setType] = useState("");
  const [status, setStatus] = useState("");
  const [date, setDate] = useState("");

  // Pagination
  const [page, setPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(50);
  const [totalProcedure, setTotalProcedure] = useState(0);

  // Fetching data
  const {
    data: procedures,
    isLoading,
    isError,
    refetch,
  } = useFetchObjects(
    ["ht-patients", page, rowsPerPage, keyword, type, status, date],
    `ht-patients/?search=${keyword}&type=${type}&status=${status}&procedure_date=${date}&page=${page}&page_size=${rowsPerPage}`,
    token
  );

  const {
    data: types,
    isLoading: isLoadingType,
    isError: isErrorType,
  } = useFetchObjects(["procedure-type"], "procedure-type", token);

  // Deletion hook
  const { handleDelete, ConfirmDialog } = useDelete("ht-patients", token);

  // Effects
  useEffect(() => {
    refetch();
  }, [keyword, type, status, date, page, rowsPerPage, refetch]);

  useEffect(() => {
    if (procedures && procedures.count !== undefined) {
      setTotalProcedure(procedures.count);
    }
  }, [procedures]);

  // Handlers
  const handlePageChange = (event, newPage) => {
    setPage(newPage);
  };

  const handleRowsPerPageChange = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(1);
  };

  const handleOpenDialog = () => {
    setOpen(!open);
  };

  // Enhanced row styling: Check procedure date first, then apply status-based color if no date condition applies.
  const getRowBackgroundColor = (procedure) => {
    // Check if procedure_date exists
    if (procedure.procedure_date) {
      const procedureDate = new Date(procedure.procedure_date);
      const today = new Date();
      const tomorrow = new Date(today);

      // Reset times for accurate date-only comparison
      today.setHours(0, 0, 0, 0);
      tomorrow.setDate(today.getDate() + 1);
      tomorrow.setHours(0, 0, 0, 0);
      procedureDate.setHours(0, 0, 0, 0);

      if (procedureDate.getTime() === today.getTime()) {
        return { backgroundColor: "#9ef0a5", color: "white" }; // Today: Green
      } else if (procedureDate.getTime() === tomorrow.getTime()) {
        return { backgroundColor: "#b1b4fa", color: "white" }; // Tomorrow: Blue
      }
    }

    // Status mapping if date conditions are not met
    const statusMapping = {
      1: { backgroundColor: "#d4edda", color: "#155724" }, // done: Light green
      2: { backgroundColor: "#f8d7da", color: "#721c24" }, // cancel: Light red
      3: { backgroundColor: "#fff3cd", color: "#856404" }, // followUP: Light yellow
      4: { backgroundColor: "#d1ecf1", color: "#0c5460" }, // inProgress: Light blue
      5: { backgroundColor: "#e2e3e5", color: "#41464b" }, // dontWant: Grey
      6: { backgroundColor: "#e2d4f7", color: "#4e3d6a" }, // nextDate: Light purple
      7: { backgroundColor: "#ffeeba", color: "#856404" }, // pending: Light orange
      8: { backgroundColor: "#c3e6cb", color: "#155724" }, // fixed: Green
      9: { backgroundColor: "#d1f0e5", color: "#0c5460" }, // flexible: Light teal
      10: { backgroundColor: "#fdfdfe", color: "black" }, // noAnswer: Almost white
      11: { backgroundColor: "#d4f1f9", color: "#0c5460" }, // information: Light cyan
      12: { backgroundColor: "#fce4ec", color: "#880e4f" }, // comingSoon: Light pink
    };

    const statusValue = procedure.status;
    return (
      statusMapping[statusValue] || { backgroundColor: "white", color: "black" }
    );
  };

  // Group procedures by date (using procedure_date_only)
  const groupedProcedures =
    procedures?.results?.reduce((groups, procedure) => {
      if (!procedure.procedure_date_only) return groups; // Skip procedures with no procedure_date_only
      const dateString = procedure.procedure_date_only;
      if (!groups[dateString]) {
        groups[dateString] = [];
      }
      groups[dateString].push(procedure);
      return groups;
    }, {}) || {}; // Ensure default value if procedures is undefined or null

  return (
    <Grid container justifyContent="center">
      <Paper sx={{ p: 2, width: "100%", mt: 2, borderRadius: 2, boxShadow: 3 }}>
        {/* Page Title */}
        <Typography
          variant="h5"
          fontWeight="bold"
          align="center"
          sx={{ pb: 2, borderBottom: "5px solid #007471" }}
        >
          {t("call_center.htPatientList")}
        </Typography>

        {/* Filters Section */}
        <Box mt={2}>
          <Stack
            direction={{ xs: "column", md: "row" }}
            spacing={2}
            justifyContent="space-between"
            alignItems="center"
          >
            {/* Search Input */}
            <TextField
              type="search"
              size="small"
              fullWidth
              label={t("call_center.search")}
              value={keyword}
              onChange={(e) => {
                setKeyword(e.target.value);
                setPage(1);
              }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon color="action" />
                  </InputAdornment>
                ),
              }}
            />

            {/* Date Filter */}
            <TextField
              type="date"
              size="small"
              label={t("call_center.filterByDate")}
              value={date}
              onChange={(e) => {
                setDate(e.target.value);
                setPage(1);
              }}
              InputLabelProps={{ shrink: true }}
              sx={{ minWidth: 200 }}
            />

            {/* Type Filter */}
            <TextField
              select
              size="small"
              label={t("call_center.filterByType")}
              value={type}
              onChange={(e) => {
                setType(e.target.value);
                setPage(1);
              }}
              sx={{ minWidth: 200 }}
            >
              {isLoadingType && (
                <Box display="flex" justifyContent="center" p={1}>
                  <CircularProgress size={20} />
                </Box>
              )}
              {isErrorType && (
                <MenuItem value="">{t("call_center.errorField")}</MenuItem>
              )}
              <MenuItem value="">{t("call_center.all")}</MenuItem>
              {types?.map((typeItem) => (
                <MenuItem value={typeItem.id} key={typeItem.id}>
                  {typeItem.name}
                </MenuItem>
              ))}
            </TextField>

            {/* Status Filter */}
            <TextField
              select
              size="small"
              label={t("call_center.filterByStatus")}
              value={status}
              onChange={(e) => {
                setStatus(e.target.value);
                setPage(1);
              }}
              sx={{ minWidth: 200 }}
            >
              <MenuItem value="">{t("call_center.all")}</MenuItem>
              <MenuItem value={1}>{t("call_center.done")}</MenuItem>
              <MenuItem value={2}>{t("call_center.cancel")}</MenuItem>
              <MenuItem value={3}>{t("call_center.followUP")}</MenuItem>
              <MenuItem value={4}>{t("call_center.inProgress")}</MenuItem>
              <MenuItem value={5}>{t("call_center.dontWant")}</MenuItem>
              <MenuItem value={6}>{t("call_center.nextDate")}</MenuItem>
              <MenuItem value={7}>{t("call_center.pending")}</MenuItem>
              <MenuItem value={8}>{t("call_center.fixed")}</MenuItem>
              <MenuItem value={9}>{t("call_center.flexible")}</MenuItem>
              <MenuItem value={10}>{t("call_center.noAnswer")}</MenuItem>
              <MenuItem value={11}>{t("call_center.information")}</MenuItem>
              <MenuItem value={12}>{t("call_center.comingSoon")}</MenuItem>
            </TextField>
          </Stack>
        </Box>

        {/* Table Section */}
        <TableContainer sx={{ mt: 3 }}>
          <Table stickyHeader size="small">
            <TableHead sx={{ bgcolor: "#f5f5f5" }}>
              <TableRow>
                <TableCell sx={tableCellStyle}>{t("call_center.id")}</TableCell>
                <TableCell sx={tableCellStyle}>
                  {t("call_center.patient")}
                </TableCell>
                <TableCell sx={tableCellStyle}>
                  {t("call_center.contact")}
                </TableCell>
                <TableCell sx={tableCellStyle}>
                  {t("call_center.procedureType")}
                </TableCell>
                <TableCell sx={tableCellStyle}>
                  {t("call_center.date")}
                </TableCell>
                <TableCell sx={tableCellStyle}>
                  {t("call_center.period")}
                </TableCell>
                <TableCell sx={tableCellStyle}>
                  {t("call_center.doctors")}
                </TableCell>
                <TableCell sx={tableCellStyle}>
                  {t("call_center.zone")}
                </TableCell>
                <TableCell sx={tableCellStyle}>
                  {t("call_center.status")}
                </TableCell>
                <TableCell sx={tableCellStyle}>
                  {t("call_center.lab")}
                </TableCell>
                <TableCell sx={tableCellStyle}>
                  {t("call_center.totalPrice")}
                </TableCell>
                <TableCell sx={tableCellStyle}>
                  {t("call_center.advance")}
                </TableCell>
                <TableCell sx={tableCellStyle}>
                  {t("call_center.nextVisitDate")}
                </TableCell>
                <TableCell sx={tableCellStyle}>
                  {t("call_center.action")}
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody sx={{ bgcolor: "#ccfcfc" }}>
              {Object.keys(groupedProcedures).length > 0 ? (
                Object.keys(groupedProcedures).map((date) => (
                  <React.Fragment key={date}>
                    <TableRow>
                      <TableCell
                        colSpan={12}
                        sx={{
                          fontWeight: "bold",
                          textAlign: "center",
                          backgroundColor: "#f0f0f0",
                        }}
                      >
                        {date}
                      </TableCell>
                    </TableRow>
                    {groupedProcedures[date].map((procedure) => (
                      <TableRow
                        key={procedure.id}
                        hover
                        style={getRowBackgroundColor(procedure)} // Apply color based on date or status
                      >
                        <TableCell sx={tableCellStyle}>
                          {procedure.id}
                        </TableCell>
                        <TableCell
                          sx={{ ...tableCellStyle, fontWeight: "bold" }}
                        >
                          {procedure.patient_name}
                        </TableCell>
                        <TableCell
                          sx={{ ...tableCellStyle, fontWeight: "bold" }}
                        >
                          {procedure?.patient_details?.phone}
                        </TableCell>
                        <TableCell
                          sx={{ ...tableCellStyle, fontWeight: "bold" }}
                        >
                          {procedure.display_procedure_type}
                        </TableCell>
                        <TableCell sx={tableCellStyle}>
                          {procedure?.procedure_date
                            ? FormatDateTime(procedure.procedure_date)
                            : ""}
                        </TableCell>
                        <TableCell
                          sx={{ ...tableCellStyle, fontWeight: "bold" }}
                        >
                          {procedure?.display_period}
                        </TableCell>
                        <TableCell sx={tableCellStyle}>
                          {procedure?.display_doctors?.length > 0 ? (
                            procedure.display_doctors.map((doc, idx) => (
                              <Chip
                                key={idx}
                                label={`${doc.first_name || ""} ${
                                  doc.last_name || ""
                                } `}
                                sx={{ mr: 0.5, mb: 0.5 }}
                                variant="outlined"
                              />
                            ))
                          ) : (
                            <Chip
                              label={t("call_center.noDoctorData", "")}
                              variant="outlined"
                            />
                          )}
                        </TableCell>
                        <TableCell sx={tableCellStyle}>
                          {procedure.zone || ""}
                        </TableCell>
                        <TableCell sx={tableCellStyle}>
                          <Chip
                            sx={{
                              fontWeight: "bold",
                              color: "blue",
                            }}
                            label={procedure?.status_name}
                          />
                        </TableCell>
                        <TableCell sx={tableCellStyle}>
                          {procedure?.display_lab?.length > 0 ? (
                            procedure.display_lab.map((lab, idx) => (
                              <Chip
                                key={idx}
                                label={lab}
                                sx={{ mr: 0.5, mb: 0.5 }}
                                variant="outlined"
                              />
                            ))
                          ) : (
                            <Chip
                              label={t("call_center.noLabData")}
                              variant="outlined"
                            />
                          )}
                        </TableCell>
                        <TableCell
                          sx={{ ...tableCellStyle, fontWeight: "bold" }}
                        >
                          {procedure.total_cost}{" "}
                          {(procedure.currency === 1 && "؋") ||
                            (procedure.currency === 2 && "$") ||
                            (procedure.currency === 3 && "€")}
                        </TableCell>
                        <TableCell sx={tableCellStyle}>
                          {procedure?.advance || ""}{" "}
                          {procedure.advance_currency === 1
                            ? "؋"
                            : procedure.advance_currency === 2
                            ? "$"
                            : procedure.advance_currency === 3
                            ? "€"
                            : ""}
                        </TableCell>
                        <TableCell sx={tableCellStyle}>
                          {procedure?.next_visit_date
                            ? FormatDateTime(procedure.next_visit_date)
                            : ""}
                        </TableCell>
                        <TableCell sx={tableCellStyle}>
                          <Box
                            display="flex"
                            justifyContent={"space-evenly"}
                            alignItems={"center"}
                          >
                            <Button
                              variant="contained"
                              color="primary"
                              size="small"
                              sx={{ textTransform: "capitalize", mr: 1 }}
                              onClick={() => {
                                setHtPatientId(procedure?.id);
                                handleOpenDialog();
                              }}
                            >
                              {t("call_center.edit")}
                            </Button>
                            {user?.user?.isAdmin && (
                              <Button
                                variant="contained"
                                color="error"
                                size="small"
                                sx={{
                                  textTransform: "capitalize",
                                  margin: "5px",
                                }}
                                onClick={() => handleDelete(procedure.id)}
                              >
                                {t("call_center.delete")}
                              </Button>
                            )}
                            <Button
                              variant="contained"
                              color="error"
                              size="small"
                              sx={{ textTransform: "capitalize" }}
                              onClick={() => {
                                navigate(
                                  "/reception/collect-ht-patient-fees/add",
                                  {
                                    state: {
                                      phone: procedure?.patient_details?.phone,
                                    },
                                  }
                                );
                              }}
                            >
                              {t("call_center.print", "Print")}
                            </Button>
                          </Box>
                        </TableCell>
                      </TableRow>
                    ))}
                  </React.Fragment>
                ))
              ) : (
                <TableRow>
                  <TableCell colSpan={12} sx={{ textAlign: "center" }}>
                    {t("call_center.noRecords")}
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>

        {/* Pagination Controls */}
        <Box
          mt={2}
          mb={1}
          display="flex"
          justifyContent="space-between"
          alignItems="center"
        >
          <Box display="flex" alignItems="center" gap={1}>
            <Typography>{t("call_center.rowsPerPage")}:</Typography>
            <FormControl size="small">
              <Select value={rowsPerPage} onChange={handleRowsPerPageChange}>
                <MenuItem value={10}>10</MenuItem>
                <MenuItem value={50}>50</MenuItem>
                <MenuItem value={100}>100</MenuItem>
                <MenuItem value={500}>500</MenuItem>
                <MenuItem value={1000}>1000</MenuItem>
              </Select>
            </FormControl>
          </Box>
          <Pagination
            count={Math.ceil(totalProcedure / rowsPerPage)}
            page={page}
            onChange={handlePageChange}
            variant="outlined"
            color="primary"
          />
        </Box>
      </Paper>

      {/* Confirm Delete Dialog */}
      <ConfirmDialog />

      {/* EDIT Dialog */}
      <Dialog maxWidth="xl" open={open} onClose={handleOpenDialog}>
        <DialogContent>
          <EditHtPatientProcedure id={htPatientId} onClose={handleOpenDialog} />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleOpenDialog}>{t("call_center.close")}</Button>
        </DialogActions>
      </Dialog>
    </Grid>
  );
};

export default HtPatientProcedureList;
